import cx from 'classnames';
import React from 'react';
import { useSelector } from 'react-redux';
import {
  MessageInput,
  MessageInputProps,
  useChannelStateContext,
  useMessageContext,
} from 'stream-chat-react';
import { makeStyles } from 'tss-react/mui';

import { MessagesSidebarContext } from 'src/context/messagesSidebarContext';
import { MessageActionsMenu as NewMessageActionsMenu } from 'src/features/channel/channel-content/message-content/MessageActionsMenu';
import { useMessagingTheme } from 'src/features/messages/messagingThemeContext';
import { AlertModal } from 'src/legacy/components/Modals';
import { RootState } from 'src/store';
import {
  BlackHeadings,
  DividersAndCardBorders,
  GraySmall,
  HoverBackground,
  orange,
  white,
} from 'src/theme/colors';

import { EditMessageForm } from './EditMessageForm';
import { MessageStatus } from './MessageStatus';
import { useChatClient } from './useChatClient';
import { useMessageComponents } from './useMessageComponents';

interface StylingProps {
  isMessageSidebarVisible: boolean;
  isSidebarTheme: boolean;
}

const useStyles = makeStyles<StylingProps>()(
  (theme, { isMessageSidebarVisible, isSidebarTheme }) => ({
    messageWrapper: {
      width: '100%',
      display: 'flex',
      [theme.breakpoints.up('sm')]: {
        '& .messageTimestampText': {
          display: 'none',
          color: GraySmall,
        },
        '& .messageActionsMenu': {
          display: 'none',
        },
        '&:hover': {
          backgroundColor: isSidebarTheme
            ? DividersAndCardBorders
            : HoverBackground,
          '& .messageTimestampText': {
            display: 'inherit',
          },
          '& .messageActionsMenu': {
            display: 'inherit',
          },
        },
      },
      [theme.breakpoints.down('xs')]: {
        // do not show timestamp on mobile
        '& .messageTimestampText': {
          display: 'none',
        },
        // do not show messages actions menu on mobile
        '& .messageActionsMenu': {
          display: 'none',
        },
      },
    },
    highlightMessage: {
      borderRadius: `1px solid ${orange}`,
      backgroundColor: '#FEF6D0',
      animation: `$highlightMessage 600ms ${theme.transitions.easing.easeIn}`,
    },
    '@keyframes highlightMessage': {
      '0%': {
        opacity: 1,
      },
      '50%': {
        opacity: 0.5,
      },
      '100%': {
        opacity: 0,
      },
    },
    metaWrapper: {
      [theme.breakpoints.up('sm')]: {
        paddingLeft: isSidebarTheme
          ? undefined
          : isMessageSidebarVisible
            ? theme.spacing(1)
            : theme.spacing(2),
      },
    },
    contentWrapper: {
      marginLeft: isSidebarTheme ? '8px' : theme.spacing(1.5),
      minWidth: 0,
    },
    messagePreviewWrapper: {
      display: 'flex',
      flexDirection: 'row',
    },
    messagePreviewBody: {
      '& pre': {
        fontSize: '12px',
        fontWeight: 700,
        backgroundColor: '#f8f8f8',
        borderRadius: 4,
        border: `1px solid #d3d3d3`,
        padding: '10px',
        wordBreak: 'break-all',
        whiteSpace: 'pre-wrap',
        margin: '0 5px 0 0',
        lineHeight: '22px',
      },
      marginLeft: theme.spacing(1.5),
      display: 'flex',
      flexDirection: 'column',
      '& div,p': {
        margin: 0,
        '& code': {
          fontSize: '12px',
          fontWeight: 400,
          backgroundColor: '#f8f8f8',
          borderRadius: 3,
          border: `1px solid #d0021b1a`,
          padding: '2px',
          wordBreak: 'break-all',
          whiteSpace: 'pre-wrap',
        },
      },
      '& ul': {
        marginLeft: theme.spacing(2),
        '& li': { fontSize: '15px', fontWeight: 400, lineHeight: '22px' },
      },
      '& ol': {
        marginLeft: theme.spacing(2),
        '& li': { fontSize: '15px', fontWeight: 400, lineHeight: '22px' },
      },
      '& .str-chat__attachment-list .str-chat__message-attachment-file--item': {
        gap: 20,
      },

      '& .str-chat__message-attachment-file--item-text': {
        position: 'relative',
        '& .str-chat__message-attachment-file--item-download': {
          position: 'absolute',
          top: '75%',
          transform: 'translateY(-50%)',
          left: 64,
        },
      },

      '& .str-chat__message-attachment-file--item .str-chat__message-attachment-file--item-text .str-chat__message-attachment-file--item-first-row .str-chat__message-attachment-file--item-name':
        {
          paddingRight: '30px',
          maxWidth: 300,
          [theme.breakpoints.down('xs')]: {
            width: '100%',
            maxWidth: 200,
          },
        },
      '& .str-chat__message-attachment-download-icon': {
        width: 20,
        height: 20,
      },

      '& .str-chat__attachment-list .str-chat__message-attachment--card': {
        borderRadius: 4,
        paddingBottom: 8,
        border: `1px solid ${DividersAndCardBorders} !important`,
        background: white,

        '& .str-chat__message-attachment-card--text': {
          whiteSpace: 'wrap',
          padding: '0px 10px',
          fontSize: '12px',
          fontWeight: 400,
          lineHeight: '18px',
        },
        '& .str-chat__message-attachment-card--url': {
          whiteSpace: 'wrap',
          padding: '8px 10px',
          fontSize: '12px',
          fontWeight: 500,
          color: BlackHeadings,
        },
        '& .str-chat__message-attachment-card--content .str-chat__message-attachment-card--title':
          {
            whiteSpace: 'wrap',
            padding: '0px 10px',
            fontSize: '12px',
            fontWeight: 500,
            color: BlackHeadings,
          },
      },
    },
  }),
);

export const MessageContent = () => {
  const { visible: isMessageSidebarVisible } = React.useContext(
    MessagesSidebarContext,
  );
  const { isSidebarTheme } = useMessagingTheme();

  const { classes } = useStyles({
    isMessageSidebarVisible,
    isSidebarTheme,
  });
  const isClient = useSelector((state: RootState) => state.user.isClient);
  const chatClient = useChatClient();
  const { channel } = useChannelStateContext();
  const {
    editing,
    setEditingState,
    clearEditingState,
    message,
    isMyMessage,
    highlighted,
  } = useMessageContext();
  const {
    MessageContentRenderer,
    MessageHeaderRenderer,
    MessageMetaRenderer,
    isNewMessageGroup,
  } = useMessageComponents();

  const [
    showDeleteMessageConfirmationModal,
    setShowDeleteMessageConfirmationModal,
  ] = React.useState(false);
  const handleDeleteClicked = () => {
    // open delete message confirmation modal
    setShowDeleteMessageConfirmationModal(true);
  };

  const handleConfirmDeleteMessage = async () => {
    setShowDeleteMessageConfirmationModal(false);
    // before deleting the message we need to check
    // if thats the last message in the channel and the
    // channel is a welcome channel.
    // if so, we need to update the channel isWelcomeChanne
    // property to false. With that, it wont show the welcome
    // channel message when deleting the last message in the channel.
    if (
      channel?.state.messages.length === 1 &&
      channel?.data?.isWelcomeChannel
    ) {
      await channel.updatePartial({ set: { isWelcomeChannel: false } });
    }
    // call delete message action with hard delete param set to true
    // in order to delete the message permanently and not show
    // its delete-state in the message list.
    await chatClient?.deleteMessage(message.id, true);
  };

  const handleEditClicked = (ev: React.MouseEvent<HTMLElement>) => {
    setEditingState(ev);
  };

  const messageInput = React.useCallback(
    (props: MessageInputProps) => (
      <EditMessageForm
        {...props}
        handleDelete={() => {
          setShowDeleteMessageConfirmationModal(true);
        }}
      />
    ),
    [],
  );

  return (
    <>
      {editing ? (
        <MessageInput
          focus
          clearEditingState={clearEditingState}
          Input={messageInput}
        />
      ) : (
        <div
          className={cx(
            classes.messageWrapper,
            'str-chat__message-team group/messageWrapper',
            {
              [classes.highlightMessage]: highlighted,
            },
          )}
        >
          <div className={classes.metaWrapper}>{MessageMetaRenderer()}</div>
          <div className={classes.contentWrapper}>
            {message && isNewMessageGroup && MessageHeaderRenderer()}
            {MessageContentRenderer()}
          </div>
          {isMyMessage() && !isClient && (
            <NewMessageActionsMenu
              onDeleteClicked={handleDeleteClicked}
              onEditClicked={handleEditClicked}
            />
          )}
          {/* Show message status only for internal users */}
          {!isClient && !isSidebarTheme && <MessageStatus />}
        </div>
      )}

      {showDeleteMessageConfirmationModal && (
        <AlertModal
          dividerPosition="top-bottom"
          isDeleteModal
          title="Are you sure you want to delete this message?"
          open={showDeleteMessageConfirmationModal}
          onSuccess={handleConfirmDeleteMessage}
          onClose={() => setShowDeleteMessageConfirmationModal(false)}
          successButtonLabel="Delete"
          noCancelButton={false}
        >
          <div className="flex">
            {MessageMetaRenderer(true)}
            <div className={classes.messagePreviewBody}>
              {MessageHeaderRenderer()}
              {MessageContentRenderer()}
            </div>
          </div>
        </AlertModal>
      )}
    </>
  );
};
